import type { ParametrosIntegracao } from "../../../services/configuracaoDeliveryFornecedor"

export type ParametrosIntegracaoModel = ParametrosIntegracao

export const ParametrosIntegracaoInicial: ParametrosIntegracaoModel = {
    interno: false,
    permiteUtilizarIfood: false,
    ativaIntegracaoIfoodV2: false,
    shopId99Food: null,
    tokenLojaPagSeguro: null,
    ativaSaipos: false,
    idPartnerSaipos: null,
    codStoreSaipos: null,
    ativaEcletica: false,
    idLojaEcletica: null,
    idRedeEcletica: null,
    loginTokenEcletica: null,
    siglaEcletica: null,
    ativarMvarandas: false,
    idEstabelecimentoMVarandas: null,
    ativarLinkedGourmet: false,
    usuarioLinkedGourmet: null,
    idLojaLinkedGourmet: null,
    ativaGetnet: false,
    clientIdGetnet: null,
    secretIdGetnet: null,
    sellerIdGetnet: null,
}

export type CampoIntegracaoKey = keyof Omit<ParametrosIntegracaoModel, "interno">

export type CampoIntegracao =
    | { tipo: "switch"; campo: CampoIntegracaoKey; label: string; descricao?: string }
    | { tipo: "text"; campo: CampoIntegracaoKey; label: string; descricao?: string }
    | { tipo: "number"; campo: CampoIntegracaoKey; label: string; descricao?: string }

export type LinkIntegracao = {
    label: string
    rota: string
    futuro?: boolean
}

/**
 * Ação que abre uma página do sistema administrativo (web) em nova aba.
 * `caminho` é relativo à URL do sistema administrativo do representante
 * (ex.: "web/configuracaodeliveryfornecedor.gerarlinkintegracao99"), montada em runtime.
 */
export type AcaoWebIntegracao = {
    label: string
    caminho: string
    descricao?: string
}

export type CardIntegracao = {
    id: string
    titulo: string
    logoUrl?: string
    icone?: "motorcycle" | "receipt"
    internoSomente?: boolean
    /** Exibe a marcação "(Em Breve)" ao lado do título — sem desabilitar a funcionalidade. */
    emBreve?: boolean
    /** Texto informativo exibido no corpo do card (ex.: orientação para acionar o suporte do parceiro). */
    descricaoCard?: string
    campos?: CampoIntegracao[]
    link?: LinkIntegracao
    /** Ação que abre uma página do sistema administrativo (web) em nova aba (ex.: gerar link de autorização). */
    acaoWeb?: AcaoWebIntegracao
}

export const CARDS_INTEGRACAO: CardIntegracao[] = [
    {
        id: "whatsappApiOficial",
        titulo: "WhatsApp API Oficial",
        logoUrl: "",
        link: { label: "Configurar integração", rota: "/servicos/modulo/integracao-whatsapp" },
    },
    {
        id: "foodyDelivery",
        titulo: "Foody Delivery",
        icone: "motorcycle",
        link: {
            label: "Configurar integração",
            rota: "/servicos/modulo/integracao-foody-delivery",
        },
    },
    {
        id: "fluxDelivery",
        titulo: "Flux Delivery",
        logoUrl: "https://clientefiel.s3.us-east-1.amazonaws.com/logos/fluxfarma.jpg",
        link: {
            label: "Configurar integração",
            rota: "/servicos/modulo/integracao-flux",
        },
    },
    {
        id: "pickngo",
        titulo: "Pick'n Go",
        logoUrl: "https://clientefiel.s3.us-east-1.amazonaws.com/images/icon-app-pickngo.png",
        link: {
            label: "Configurar integração",
            rota: "/servicos/modulo/integracao-pickngo",
        },
    },
    {
        id: "ifood",
        titulo: "iFood",
        logoUrl: "https://clientefiel.s3.us-east-1.amazonaws.com/logos/ifood-logo.png",
        campos: [
            {
                tipo: "switch",
                campo: "ativaIntegracaoIfoodV2",
                label: "Ativar / desativar integração iFood",
            },
        ],
        link: { label: "Configurar integração iFood", rota: "/servicos/modulo/integracao-ifood" },
    },
    {
        id: "noveNoveFood",
        titulo: "99 Food",
        logoUrl: "https://clientefiel.s3.us-east-1.amazonaws.com/logos/5fd3613d19cc2-lg.png",
        campos: [
            {
                tipo: "text",
                campo: "shopId99Food",
                label: "Código do estabelecimento (Shop ID)",
                descricao:
                    "Inserir o código do estabelecimento na 99 Food para ativar a integração.",
            },
        ],
        acaoWeb: {
            label: "Gerar link de autorização",
            caminho: "web/configuracaodeliveryfornecedor.gerarlinkintegracao99",
            descricao:
                "Gera o link para o cliente autorizar a integração com a 99 Food (abre em nova aba).",
        },
    },
    {
        id: "pagseguro",
        titulo: "PagSeguro",
        logoUrl: "https://clientefiel.s3.amazonaws.com/images/logopagseguro.png",
        campos: [{ tipo: "text", campo: "tokenLojaPagSeguro", label: "Token da Loja" }],
        link: {
            label: "Gerar Token da Loja",
            rota: "/servicos/modulo/integracao-pagseguro",
        },
    },
    {
        id: "rede",
        titulo: "Rede",
        logoUrl: "",
        emBreve: true,
        link: {
            label: "Configurar integração",
            rota: "/servicos/modulo/integracao-rede",
        },
    },
    {
        id: "saipos",
        titulo: "Saipos",
        logoUrl:
            "https://clientefielsp.s3.sa-east-1.amazonaws.com/parceirosIntegracao/saipos_logo.png",
        campos: [
            { tipo: "switch", campo: "ativaSaipos", label: "Ativar Saipos" },
            { tipo: "text", campo: "idPartnerSaipos", label: "ID Partner" },
            { tipo: "text", campo: "codStoreSaipos", label: "Código da Loja" },
        ],
    },
    {
        id: "repediu",
        titulo: "Repediu",
        logoUrl: "",
        link: { label: "Configurar integração", rota: "/servicos/modulo/integracao-repediu" },
    },
    {
        id: "digisac",
        titulo: "DigiSac",
        logoUrl: "",
        link: { label: "Configurar integração", rota: "/servicos/modulo/integracao-digisac" },
    },
    {
        id: "ecletica",
        titulo: "Eclética",
        logoUrl: "https://www.appclientefiel.com.br/images/partnar/ecletica.png",
        campos: [
            { tipo: "switch", campo: "ativaEcletica", label: "Ativar Eclética" },
            { tipo: "text", campo: "idLojaEcletica", label: "ID da Loja" },
            { tipo: "text", campo: "idRedeEcletica", label: "ID da Rede" },
            { tipo: "text", campo: "loginTokenEcletica", label: "Login Token" },
            { tipo: "text", campo: "siglaEcletica", label: "Sigla" },
        ],
    },
    {
        id: "menew",
        titulo: "Linx/MENEW (Antigo MVarandas)",
        logoUrl: "https://www.appclientefiel.com.br/images/partnar/mvvaranda.png",
        campos: [
            { tipo: "switch", campo: "ativarMvarandas", label: "Ativar MENEW" },
            { tipo: "number", campo: "idEstabelecimentoMVarandas", label: "ID do Estabelecimento" },
        ],
    },
    {
        id: "linkedGourmet",
        titulo: "Linked Gourmet",
        logoUrl:
            "https://clientefielsp.s3.sa-east-1.amazonaws.com/parceirosIntegracao/linked_gourmet_logo.jpg",
        campos: [
            { tipo: "switch", campo: "ativarLinkedGourmet", label: "Ativar Linked Gourmet" },
            { tipo: "text", campo: "usuarioLinkedGourmet", label: "ID do Usuário" },
            { tipo: "text", campo: "idLojaLinkedGourmet", label: "ID da Loja" },
        ],
    },
    {
        id: "icomanda",
        titulo: "iComanda",
        logoUrl: "/images/icomanda.png",
        descricaoCard: "Acione o suporte do iComanda para ativarem a integração.",
    },
    {
        id: "nimbus",
        titulo: "Nimbus",
        logoUrl: "https://clientefielsp.s3-sa-east-1.amazonaws.com/representantes/nimbus.png",
        descricaoCard: "Acione o suporte do Nimbus para ativarem a integração.",
    },
    {
        id: "plugNotasFiscal",
        titulo: "Emissão fiscal (NFC-e)",
        icone: "receipt",
        descricaoCard:
            "CNPJ na PlugNotas, emissão automática, regime tributário, CFOP/CSOSN e formas de pagamento elegíveis.",
        link: { label: "Configuração fiscal", rota: "/fiscal/configuracao-fiscal" },
    },
    {
        id: "opendelivery",
        titulo: "OpenDelivery",
        logoUrl: "/images/opendelivery.png",
        link: {
            label: "Configurar integração",
            rota: "/servicos/modulo/integracao-opendelivery",
        },
    },
    {
        id: "softcom",
        titulo: "Softcom",
        logoUrl: "",
        descricaoCard: "Acione o suporte do JSoftCom para ativarem a integração.",
    },
    {
        id: "mgmSistemas",
        titulo: "MGM Sistemas",
        logoUrl: "",
        descricaoCard: "Acione o suporte do MGM para ativarem a integração.",
    },
    {
        id: "uber",
        titulo: "Uber",
        logoUrl: "https://clientefielsp.s3.sa-east-1.amazonaws.com/integracao/logo-uber.jpg",
        internoSomente: true,
        link: { label: "Configurar integração", rota: "/servicos/modulo/integracao-uber" },
    },
    {
        id: "getnet",
        titulo: "GetNet",
        logoUrl:
            "https://clientefielsp.s3.sa-east-1.amazonaws.com/parceirosIntegracao/getnet_logo.png",
        internoSomente: true,
        campos: [
            { tipo: "switch", campo: "ativaGetnet", label: "Ativar GetNet" },
            { tipo: "text", campo: "clientIdGetnet", label: "Client ID" },
            { tipo: "text", campo: "secretIdGetnet", label: "Secret ID" },
            { tipo: "text", campo: "sellerIdGetnet", label: "Seller ID" },
        ],
    },
]
